import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { IconButton, Spinner } from 'components';
import { Row } from 'components/layout';
import ProfileWelcomePopup from 'components/candidate/ProfileWelcomePopup';
import Modal from 'components/Modal';
import { USERINFO_LABELS } from 'hooks/useCandidateProfile';
import { useResponsive } from 'hooks/useResponsive';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';

import BasicInfo from '../BasicInfo';
import CVUploadBanner from '../CVUploadBanner';
import DemographicInfo from '../DemographicInfo';
import Education from '../Education';
import Experience from '../Experience';
import ExtraCurricular from '../ExtraCurricular';
import FloatingCVSection from '../FloatingCVSection';
import MobileProfileProgress from '../MobileProfileProgress';
import Sidebar from '../Sidebar';
import Skills from '../Skills';

import { getSchoolInfo, getUniversityInfo } from '../Education/utils';
import { getExperienceInfo } from '../Experience/utils';
import { getSkillsInfo } from '../Skills/utils';
import { getExtraCurricularInfo } from '../ExtraCurricular/utils';
import styles from './BuildProfile.module.scss';

export const BuildProfile: React.FC = memo(() => {
  const [t] = useTranslation('candidate.profile');
  const screens = useResponsive();
  const router = useRouter();
  const { toggleUserInfoCompleteStatus, updateCandidate } =
    useCandidateProfile();

  const { candidate } = useAuth();

  const [canShowCVBanner, setCanShowCVBanner] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);

  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [showMobileProfileProgress, setShowMobileProfileProgress] =
    useState<boolean>(true);

  const basicInfoRef = useRef<HTMLDivElement | null>(null);
  const educationRef = useRef<HTMLDivElement | null>(null);
  const experienceRef = useRef<HTMLDivElement | null>(null);
  const extraCurricularRef = useRef<HTMLDivElement | null>(null);
  const skillsRef = useRef<HTMLDivElement | null>(null);
  const demographicInfoRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (candidate) {
      const {
        first_name,
        last_name,
        gender,
        ethnicities,
        physical_disability,
        congnitive_disability,
        socio_economic_status,
        email,
        resume_url,
      } = candidate;
      setResumeUrl(resume_url);

      const universityInfo = getUniversityInfo(candidate);
      const schoolInfo = getSchoolInfo(candidate);
      const experienceInfo = getExperienceInfo(candidate);
      const skillsInfo = getSkillsInfo(candidate);
      const extraCurricularInfo = getExtraCurricularInfo(candidate);

      if (first_name && last_name && email) {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.BASIC_INFO, true);
      } else {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.BASIC_INFO, false);
      }
      if (universityInfo.length > 0 || schoolInfo.length > 0) {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.EDUCATION, true);
      } else {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.EDUCATION, false);
      }
      if (experienceInfo.length > 0) {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.EXPERIENCE, true);
      } else {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.EXPERIENCE, false);
      }
      if (extraCurricularInfo.length > 0) {
        toggleUserInfoCompleteStatus(
          USERINFO_LABELS.EXTRA_CURRICULAR_ACTIVITIES,
          true
        );
      } else {
        toggleUserInfoCompleteStatus(
          USERINFO_LABELS.EXTRA_CURRICULAR_ACTIVITIES,
          false
        );
      }
      if (skillsInfo.length > 0) {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.SKILLS, true);
      } else {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.SKILLS, false);
      }
      if (
        gender &&
        ethnicities &&
        ethnicities.data.length > 0 &&
        physical_disability &&
        congnitive_disability &&
        socio_economic_status
      ) {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.DEMOGRAPHIC_INFO, true);
      } else {
        toggleUserInfoCompleteStatus(USERINFO_LABELS.DEMOGRAPHIC_INFO, false);
      }
    }
  }, [candidate]);

  const hideCandidateProfileSplashScreen = () => {
    updateCandidate({ show_profile_splash_screen: false }, true);
  };

  const onContinue = (label: USERINFO_LABELS) => {
    let top = window.scrollY;
    switch (label) {
      case USERINFO_LABELS.BASIC_INFO:
        top = basicInfoRef.current?.getBoundingClientRect().top || 0;
        break;
      case USERINFO_LABELS.EDUCATION:
        top = educationRef.current?.getBoundingClientRect().top || 0;
        break;
      case USERINFO_LABELS.EXPERIENCE:
        top = experienceRef.current?.getBoundingClientRect().top || 0;
        break;
      case USERINFO_LABELS.EXTRA_CURRICULAR_ACTIVITIES:
        top = extraCurricularRef.current?.getBoundingClientRect().top || 0;
        break;
      case USERINFO_LABELS.SKILLS:
        top = skillsRef.current?.getBoundingClientRect().top || 0;
        break;
      case USERINFO_LABELS.DEMOGRAPHIC_INFO:
        top = demographicInfoRef.current?.getBoundingClientRect().top || 0;
        break;
      default:
        break;
    }
    window.scrollTo({
      top: top + window.scrollY - 90,
      behavior: 'smooth',
    });
  };
  const onSidebarLabelClick = (label: USERINFO_LABELS) => {
    onContinue(label);
  };

  const onCloseCVBanner = useCallback(() => setCanShowCVBanner(false), []);

  const handleScroll = useCallback(() => {
    if (window.scrollY > 0) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!candidate) {
    return (
      <div className={styles.spinner}>
        <Spinner />
      </div>
    );
  }

  return (
    <section className={styles.container}>
      {screens.sm && (
        <header className={styles.header}>
          <p className={styles.headerTitle}>{t('build-profile.profile')}</p>
          <div className={styles.headerActions}>
            <IconButton
              iconName="icon_notification-bell"
              className={styles.headerIcon}
              onClick={() => router.push(PAGE_ROUTES.CANDIDATE_NOTIFICATIONS)}
            />
            <IconButton
              iconName="icon_gear"
              className={styles.headerIcon}
              onClick={() => router.push(PAGE_ROUTES.CANDIDATE_SETTINGS)}
            />
          </div>
        </header>
      )}
      <section className={cn(styles.content)}>
        {!resumeUrl && canShowCVBanner ? (
          <div className={styles['cv-upload-banner']}>
            <CVUploadBanner onClose={onCloseCVBanner} />
          </div>
        ) : null}

        {screens.sm_desktop_down && showMobileProfileProgress && (
          <MobileProfileProgress
            onLabelClick={onSidebarLabelClick}
            hideProfileProgress={() => setShowMobileProfileProgress(false)}
          />
        )}

        <Row justify="center" className={cn(styles.main)}>
          {candidate.show_profile_splash_screen && (
            <Modal
              isOpen={candidate.show_profile_splash_screen}
              onRequestClose={hideCandidateProfileSplashScreen}
              contentClassName={styles['welcome-popup-modal']}
            >
              <ProfileWelcomePopup onClose={hideCandidateProfileSplashScreen} />
            </Modal>
          )}
          <Sidebar
            onLabelClick={onSidebarLabelClick}
            className={styles.sidebar}
          />
          <section className={styles.col2}>
            <div ref={basicInfoRef}>
              <BasicInfo className={styles['profile-section']} />
            </div>
            <div ref={educationRef}>
              <Education className={styles['profile-section']} />
            </div>
            <div ref={experienceRef}>
              <Experience className={styles['profile-section']} />
            </div>
            <div ref={extraCurricularRef}>
              <ExtraCurricular className={styles['profile-section']} />
            </div>
            <div ref={skillsRef}>
              <Skills className={styles['profile-section']} />
            </div>
            <div ref={demographicInfoRef}>
              <DemographicInfo className={styles.section} />
            </div>
          </section>
        </Row>
      </section>
      {screens.sm_desktop_down && <FloatingCVSection scrolled={isScrolled} />}
    </section>
  );
});

BuildProfile.displayName = 'BuildProfile';
